import * as React from "react"
import { cn } from "@merels/ui"
import { useTheme } from "../../../contexts/ThemeContext"
import { IconButton } from "../../library/IconButton/IconButton"
import { GearIcon } from "../../library/Icons/GearIcon"

/** A secondary action shown in the tile's overlay, beside Configure. */
export interface GameTileAction {
  label: string
  icon: React.ReactNode
  onClick: () => void
  disabled?: boolean
  testId?: string
}

export interface GameTileProps {
  displayName: string
  publisherSlug?: string
  description?: string
  thumbnail?: React.ReactNode
  onSelect: () => void
  onConfigure?: () => void
  disabled?: boolean
  disabledNote?: string
  onDisabledSelect?: () => void
  className?: string
  'data-testid'?: string
  tabIndex?: number
  isFocused?: boolean
  badge?: React.ReactNode
  actions?: GameTileAction[]
}

export const GameTile = React.forwardRef<HTMLDivElement, GameTileProps>(
  ({ displayName, publisherSlug, thumbnail, onSelect, onConfigure, disabled = false, disabledNote, onDisabledSelect, className, 'data-testid': testId, tabIndex, isFocused, badge, actions = [] }, ref) => {
    const { getAppColor } = useTheme()
    const appColorShades = getAppColor()
    const publisherName = publisherSlug?.split('-').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
    const canOpenDisabledDetails = disabled && Boolean(onDisabledSelect)

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        e.stopPropagation()
        if (disabled) {
          onDisabledSelect?.()
          return
        }
        onSelect()
      }
    }

    return (
      <>
        <div
          ref={ref}
          className={cn(
            "relative border border-basic rounded-lg overflow-hidden bg-background transition-colors cursor-pointer group flex flex-col w-full max-w-[200px]",
            isFocused && "outline-none",
            disabled && !canOpenDisabledDetails && "opacity-50 cursor-not-allowed",
            disabled && canOpenDisabledDetails && "opacity-50 cursor-pointer",
            className
          )}
          style={{
            ...(isFocused ? {
              borderColor: appColorShades.main,
              backgroundColor: appColorShades.background,
              boxShadow: `0 0 0 2px ${appColorShades.main}, 0 0 0 4px ${appColorShades.background}`,
            } : {}),
          }}
          onMouseEnter={(e) => {
            if (disabled && !canOpenDisabledDetails) return
            if (!isFocused) {
              e.currentTarget.style.borderColor = appColorShades.main
              e.currentTarget.style.backgroundColor = appColorShades.background
            }
          }}
          onMouseLeave={(e) => {
            if (!isFocused) {
              e.currentTarget.style.borderColor = ''
              e.currentTarget.style.backgroundColor = ''
            }
          }}
          onClick={() => {
            if (disabled) {
              onDisabledSelect?.()
              return
            }
            onSelect()
          }}
          onKeyDown={handleKeyDown}
          tabIndex={tabIndex}
          role="button"
          aria-disabled={disabled ? 'true' : 'false'}
          aria-label={`Select ${displayName}`}
          data-testid={testId}
        >
          <div className="pointer-events-none absolute right-2 top-2 z-10 flex gap-1 rounded-md bg-background/85 p-1 opacity-0 shadow-sm transition-all duration-150 group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100">
            {typeof onConfigure === 'function' ? (
              <IconButton
                onClick={(e) => {
                  e.stopPropagation()
                  onConfigure()
                }}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors hover:bg-accent hover:text-inverse focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-focus flex-shrink-0"
                aria-label="Configure game"
                icon={<GearIcon width={18} height={18} />}
              />
            ) : null}
            {actions.map((action) => (
              <IconButton
                key={action.label}
                onClick={(e) => {
                  e.stopPropagation()
                  action.onClick()
                }}
                disabled={action.disabled}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors hover:bg-accent hover:text-inverse focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-focus disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-current flex-shrink-0"
                aria-label={action.label}
                title={action.label}
                data-testid={action.testId}
                icon={action.icon}
              />
            ))}
          </div>

          {/* Thumbnail - 200:120 aspect ratio */}
          <div className="w-full bg-muted-bg flex items-center justify-center flex-shrink-0 overflow-hidden" style={{ aspectRatio: '200/120' }}>
            {thumbnail || (
              <div className="text-muted text-sm">No thumbnail</div>
            )}
          </div>

          {badge ? (
            <div className="absolute left-2 top-2 z-10">
              {badge}
            </div>
          ) : null}

          {/* Title block stays full width; actions live in the thumbnail overlay. */}
          <div className="h-[60px] p-2 flex items-center flex-shrink-0 min-h-0">
            <div className="min-w-0 w-full">
              <h3 className="text-lg font-semibold truncate">{displayName}</h3>
              {publisherSlug && <div className="text-[10px] text-muted truncate">by {publisherSlug === 'you' ? 'you' : publisherName}</div>}
              {disabledNote && (
                <div className="text-xs text-muted mt-1 truncate">{disabledNote}</div>
              )}
            </div>
          </div>
        </div>
      </>
    )
  }
)
GameTile.displayName = "GameTile"
